import React, { createContext, useContext } from 'react'

/**
 * **Angetippt** — der Zustand, in dem die Leiste eines **geklebten** Zettels
 * offen steht.
 *
 * Ausdrücklich NICHT „ausgewählt": Ein geklebter Zettel gerät in keine Auswahl
 * (`selectable: false` in `noteToFlowNode`), damit keine Sammelaktion ihn
 * eigens ausnehmen muss. Angetippt trägt genau EINE Wirkung — die
 * **Options-Leiste** mit dem einen Knopf **Ablösen**. Kein **Auswahlrahmen**,
 * keine Griffe, kein **Tauschpunkt**, keine **Anlegepunkte**.
 *
 * | Hinein | Heraus |
 * |---|---|
 * | Klick auf den **Klebestreifen** | Klick daneben (Fläche, anderer Zettel) |
 * | **Tab** hält an ihm an | Escape, Tab weiter, Ablösen, Ansicht bewegen |
 *
 * **Über einen Context und nicht über die `data` des Nodes:** Der Node-Abgleich
 * in `CanvasFlow` hält `data` mühsam stabil (er vergleicht `note`,
 * `startEditing`, `editingEnabled`, `editRequest`) — ein Feld, das bei jedem
 * Antippen wechselt, baute den ganzen Baum neu. Dieselbe Überlegung wie bei
 * `useSoloSelected`, nur dass die Auskunft dort aus React Flows Store kommt und
 * hier aus dem Canvas.
 *
 * Der Vergleich ist ein Boolean je Zettel: Beim Antippen rendern alle Nodes
 * einmal und alle bis auf zwei stellen fest, dass sich nichts geändert hat.
 */
export interface CanvasTapeState {
  /** Der **angetippte** Zettel, oder `null`. */
  tapped: string | null
  /**
   * Der Zettel, dessen Streifen sich gerade **abzieht**.
   *
   * Getrennt vom Angetippten, weil die beiden verschiedene Zeitpunkte meinen:
   * Das Abziehen läuft, WÄHREND die Zeile noch klebt — erst wenn die Bewegung
   * durch ist, fällt `tape` auf `null`. Ohne diesen Zwischenzustand
   * verschwände der Streifen im selben Bild, in dem er sich noch zurückziehen
   * soll.
   */
  peeling: string | null
}

const NOTHING: CanvasTapeState = { tapped: null, peeling: null }

const CanvasTappedContext = createContext<CanvasTapeState>(NOTHING)

export const CanvasTappedProvider = CanvasTappedContext.Provider

/** Ob DIESER Zettel gerade angetippt ist. */
export function useIsTapped(noteId: string): boolean {
  return useContext(CanvasTappedContext).tapped === noteId
}

/** Ob sich der Streifen DIESES Zettels gerade abzieht. */
export function useIsPeeling(noteId: string): boolean {
  return useContext(CanvasTappedContext).peeling === noteId
}

/**
 * Der ganze Zustand — für die Stellen, die ihn nicht für sich selbst, sondern
 * für den Canvas brauchen (die **Tab-Reihenfolge** etwa braucht den
 * Angetippten als Anker).
 */
export function useCanvasTapeState(): CanvasTapeState {
  return useContext(CanvasTappedContext)
}

/** Nur damit `React` im Modul benutzt ist — der Provider ist ein Bauteil. */
export type CanvasTappedProviderProps = React.ProviderProps<CanvasTapeState>
